import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { 
  Swords, 
  Send, 
  Users, 
  Globe, 
  Lock, 
  Loader2,
  ArrowLeft,
  Flame,
  ThumbsUp,
  Laugh,
  Crown,
  AlertTriangle,
  Clock,
  Eye,
  Timer,
  Play,
  MessageCircle,
  Trash2,
  EyeOff,
  CheckCircle,
  Radio
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Room = () => {
  const { roomId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [room, setRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [reactions, setReactions] = useState({ fire: 0, thumbs_up: 0, laugh: 0, total: 0 });
  const [userVote, setUserVote] = useState(null);
  const [endRequested, setEndRequested] = useState(false);
  
  // Audience comments
  const [audienceComments, setAudienceComments] = useState([]);
  const [newAudienceComment, setNewAudienceComment] = useState('');
  const [sendingComment, setSendingComment] = useState(false);
  const [showAudiencePanel, setShowAudiencePanel] = useState(true);
  
  // Inactivity state
  const [activityStatus, setActivityStatus] = useState(null);
  const [showInactivityWarning, setShowInactivityWarning] = useState(false);
  const [warningCountdown, setWarningCountdown] = useState(60);
  
  // Expiry state for waiting rooms
  const [showExpiryWarning, setShowExpiryWarning] = useState(false);
  const [expiryCountdown, setExpiryCountdown] = useState(0);
  
  // Debate timer state (for live debates)
  const [debateTimeRemaining, setDebateTimeRemaining] = useState(null);
  const [debateTimerWarning, setDebateTimerWarning] = useState(false);
  
  // Ready state tracking (both joined, waiting for messages)
  const [participantAMessageCount, setParticipantAMessageCount] = useState(0);
  const [participantBMessageCount, setParticipantBMessageCount] = useState(0);
  
  // Resume banner
  const [showResumeBanner, setShowResumeBanner] = useState(false);
  
  // Delete/Hide state
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  const messagesEndRef = useRef(null);
  const lastMessageTime = useRef(null);

  // Determine user's role
  const isParticipantA = room?.participant_a_id === user?.user_id;
  const isParticipantB = room?.participant_b_id === user?.user_id;
  const isParticipant = isParticipantA || isParticipantB;
  const isAudience = !isParticipant && room?.room_type === 'public';
  const isCreator = room?.created_by === user?.user_id;

  // Fetch room data
  const fetchRoom = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/rooms/${roomId}`);
      setRoom(response.data);
      
      if (response.data.status === 'ended') {
        navigate(`/rooms/${roomId}/result`);
      }
      
      // Update debate timer from room data
      if (response.data.status === 'live' && response.data.time_remaining_seconds !== undefined) {
        setDebateTimeRemaining(response.data.time_remaining_seconds);
      }
      
      // Show resume banner if creator returns and room is now ready/live
      if ((response.data.status === 'ready' || response.data.status === 'live') && 
          response.data.created_by === user?.user_id && 
          response.data.participant_b_id &&
          !isParticipant) {
        setShowResumeBanner(true);
      }
    } catch (error) {
      console.error('Error fetching room:', error);
      toast.error('Room not found');
      navigate('/');
    } finally {
      setLoading(false);
    }
  }, [roomId, navigate, user, isParticipant]);

  // Fetch activity status
  const fetchActivityStatus = useCallback(async () => {
    if (!room || room.status === 'ended') return;
    
    try {
      const response = await axios.get(`${API_URL}/api/rooms/${roomId}/activity-status`);
      setActivityStatus(response.data);
      
      // Track message counts for ready state
      setParticipantAMessageCount(response.data.participant_a_message_count || 0);
      setParticipantBMessageCount(response.data.participant_b_message_count || 0);
      
      // Handle waiting room expiry warning (24 hours)
      if (room.status === 'waiting' && response.data.will_expire) {
        setShowExpiryWarning(true);
        setExpiryCountdown(response.data.seconds_until_expiry);
      }
      
      // Handle ready room inactivity warning
      if (room.status === 'ready' && response.data.show_warning) {
        setShowInactivityWarning(true);
        setWarningCountdown(response.data.seconds_until_auto_end);
      } else if (room.status === 'ready') {
        setShowInactivityWarning(false);
      }
      
      // Handle live room - debate timer + inactivity
      if (room.status === 'live') {
        setDebateTimeRemaining(response.data.debate_remaining_seconds);
        setDebateTimerWarning(response.data.debate_will_timeout);
        
        if (response.data.show_warning) {
          setShowInactivityWarning(true);
          setWarningCountdown(response.data.seconds_until_auto_end);
        } else {
          setShowInactivityWarning(false);
        }
      }
    } catch (error) {
      console.error('Error fetching activity status:', error);
    }
  }, [room, roomId]);

  // Fetch messages (polling)
  const fetchMessages = useCallback(async () => {
    try {
      const since = lastMessageTime.current || '';
      const url = since 
        ? `${API_URL}/api/rooms/${roomId}/messages?since=${encodeURIComponent(since)}`
        : `${API_URL}/api/rooms/${roomId}/messages`;
      
      const response = await axios.get(url);
      const newMessages = response.data.messages || [];
      
      if (newMessages.length > 0) {
        setMessages(prev => {
          const existingIds = new Set(prev.map(m => m.message_id));
          const filtered = newMessages.filter(m => !existingIds.has(m.message_id));
          if (filtered.length > 0) {
            lastMessageTime.current = filtered[filtered.length - 1].created_at;
            return [...prev, ...filtered];
          }
          return prev;
        });
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  }, [roomId]);

  // Fetch reactions
  const fetchReactions = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/rooms/${roomId}/reactions`);
      setReactions(response.data);
    } catch (error) {
      console.error('Error fetching reactions:', error);
    }
  }, [roomId]);

  // Initial fetch
  useEffect(() => {
    fetchRoom();
    fetchMessages();
    fetchReactions();
  }, [fetchRoom, fetchMessages, fetchReactions]);

  // Polling interval
  useEffect(() => {
    if (!room || room.status === 'ended') return;
    
    const interval = setInterval(() => {
      fetchRoom();
      fetchMessages();
      fetchActivityStatus();
      if (room.room_type === 'public') {
        fetchReactions();
        fetchAudienceComments();
      }
    }, 2000);
    
    return () => clearInterval(interval);
  }, [room, fetchRoom, fetchMessages, fetchReactions, fetchActivityStatus]);

  // Fetch audience comments
  const fetchAudienceComments = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/rooms/${roomId}/audience-comments`);
      setAudienceComments(response.data.comments || []);
    } catch (error) {
      console.error('Error fetching audience comments:', error);
    }
  }, [roomId]);

  // Initial fetch for audience comments
  useEffect(() => {
    if (room?.room_type === 'public') {
      fetchAudienceComments();
    }
  }, [room, fetchAudienceComments]);

  // Post audience comment
  const handlePostAudienceComment = async (e) => {
    e.preventDefault();
    if (!newAudienceComment.trim() || sendingComment || !user) return;
    
    setSendingComment(true);
    try {
      const formData = new FormData();
      formData.append('comment_text', newAudienceComment.trim());
      
      await axios.post(`${API_URL}/api/rooms/${roomId}/audience-comment`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setNewAudienceComment('');
      fetchAudienceComments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post comment');
    } finally {
      setSendingComment(false);
    }
  };

  // Warning countdown timer
  useEffect(() => {
    if (!showInactivityWarning || warningCountdown <= 0) return;
    
    const timer = setInterval(() => {
      setWarningCountdown(prev => {
        if (prev <= 1) {
          handleForceEnd();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [showInactivityWarning, warningCountdown]);

  // Expiry countdown timer
  useEffect(() => {
    if (!showExpiryWarning || expiryCountdown <= 0) return;
    
    const timer = setInterval(() => {
      setExpiryCountdown(prev => {
        if (prev <= 1) {
          handleAutoExpire();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [showExpiryWarning, expiryCountdown]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !isParticipant || sending) return;
    
    setSending(true);
    try {
      await axios.post(`${API_URL}/api/rooms/${roomId}/messages`, {
        message_text: newMessage.trim()
      }, { withCredentials: true });
      
      setNewMessage('');
      setShowInactivityWarning(false);
      fetchMessages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleJoinRoom = async () => {
    try {
      await axios.post(`${API_URL}/api/rooms/${roomId}/join`, {}, { withCredentials: true });
      toast.success('Joined the debate!');
      fetchRoom();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join room');
    }
  };

  const handleVote = async (voteFor) => {
    if (!user || isParticipant || userVote) return;
    
    try {
      await axios.post(`${API_URL}/api/rooms/${roomId}/vote`, {
        vote_for: voteFor
      }, { withCredentials: true });
      
      setUserVote(voteFor);
      toast.success(`Voted for ${voteFor === 'A' ? room.participant_a_name : room.participant_b_name}!`);
      fetchRoom();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to vote');
    }
  };

  const handleReact = async (reactionType) => {
    if (!user) return;
    
    try {
      await axios.post(`${API_URL}/api/rooms/${roomId}/react`, {
        reaction_type: reactionType
      }, { withCredentials: true });
      
      fetchReactions();
    } catch (error) {
      // Silently fail for reactions
    }
  };

  const handleEndDebate = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/rooms/${roomId}/end`, {}, { withCredentials: true });
      
      if (response.data.status === 'ended') {
        navigate(`/rooms/${roomId}/result`);
      } else {
        setEndRequested(true);
        toast.info('Waiting for opponent to end the debate...');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to end debate');
    }
  };

  const handleForceEnd = async () => {
    try {
      await axios.post(`${API_URL}/api/rooms/${roomId}/force-end?reason=inactivity`);
      navigate(`/rooms/${roomId}/result`);
    } catch (error) {
      console.error('Force end error:', error);
    }
  };

  const handleAutoExpire = async () => {
    try {
      await axios.post(`${API_URL}/api/rooms/${roomId}/force-end?reason=expired`);
      toast.info('Room expired - no participants joined');
      navigate('/');
    } catch (error) {
      console.error('Auto expire error:', error);
    }
  };

  // Delete room (WAITING state only)
  const handleDeleteRoom = async () => {
    if (!isCreator || room.status !== 'waiting' || room.participant_b_id) {
      toast.error('Cannot delete this room');
      return;
    }
    
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/api/rooms/${roomId}`, { withCredentials: true });
      toast.success('Room deleted successfully');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete room');
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  // Hide room from profile (ENDED state only - soft delete)
  const handleHideRoom = async () => {
    if (!isParticipant || room.status !== 'ended') {
      toast.error('Cannot hide this room');
      return;
    }
    
    try {
      await axios.post(`${API_URL}/api/rooms/${roomId}/hide`, {}, { withCredentials: true });
      toast.success('Room hidden from your profile');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to hide room');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-orange-500" />
      </div>
    );
  }

  if (!room) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <p className="text-white">Room not found</p>
      </div>
    );
  }

  // Split messages by side
  const messagesA = messages.filter(m => m.side === 'A');
  const messagesB = messages.filter(m => m.side === 'B');

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex flex-col">
      {/* Header */}
      <div className="bg-black/30 backdrop-blur-lg border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate('/')}
                className="text-white hover:bg-white/10"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              
              <div className="flex items-center gap-3">
                {room.topic_image_url && (
                  <img 
                    src={`${API_URL}${room.topic_image_url}`} 
                    alt="Topic"
                    className="w-12 h-12 rounded-lg object-cover"
                  />
                )}
                <div>
                  <h1 className="text-white font-bold text-lg line-clamp-1">{room.topic}</h1>
                  <div className="flex items-center gap-2 text-sm">
                    {room.room_type === 'public' ? (
                      <span className="flex items-center gap-1 text-green-400">
                        <Globe className="h-3 w-3" /> PUBLIC
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-purple-400">
                        <Lock className="h-3 w-3" /> PRIVATE
                      </span>
                    )}
                    {room.status === 'waiting' && (
                      <span className="flex items-center gap-1 text-yellow-400">
                        <Clock className="h-3 w-3" /> WAITING FOR OPPONENT
                      </span>
                    )}
                    {room.status === 'ready' && (
                      <span className="flex items-center gap-1 text-blue-400">
                        <CheckCircle className="h-3 w-3" /> READY
                      </span>
                    )}
                    {room.status === 'live' && (
                      <span className="flex items-center gap-1 text-red-400">
                        <Radio className="h-3 w-3 animate-pulse" /> LIVE
                        {debateTimeRemaining !== null && (
                          <span className="ml-1 font-mono text-xs">
                            {Math.floor(debateTimeRemaining / 60)}:{(debateTimeRemaining % 60).toString().padStart(2, '0')}
                          </span>
                        )}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            {isParticipant && room.status === 'live' && (
              <Button
                onClick={handleEndDebate}
                variant="outline"
                size="sm"
                className={`border-red-500 text-red-400 hover:bg-red-500/20 ${endRequested ? 'opacity-50' : ''}`}
                disabled={endRequested}
              >
                {endRequested ? 'Waiting...' : 'End Debate'}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Resume Banner */}
      {showResumeBanner && (
        <div className="bg-gradient-to-r from-green-500/20 to-blue-500/20 border-b border-green-500/50 px-4 py-3">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2 text-green-400">
              <Play className="h-4 w-4" />
              <span className="text-sm font-medium">Your room is now live — someone joined!</span>
            </div>
            <Button
              size="sm"
              onClick={() => {
                setShowResumeBanner(false);
                fetchRoom();
              }}
              className="bg-green-500 hover:bg-green-600 text-white"
            >
              Resume Debate
            </Button>
          </div>
        </div>
      )}

      {/* Inactivity Warning */}
      {showInactivityWarning && room.status === 'active' && (
        <div className="bg-yellow-500/20 border-b border-yellow-500/50 px-4 py-2">
          <div className="max-w-7xl mx-auto flex items-center justify-center gap-2 text-yellow-400">
            <AlertTriangle className="h-4 w-4 animate-pulse" />
            <span className="text-sm font-medium">
              ⚠️ Debate will end in {warningCountdown}s due to inactivity
            </span>
            <Timer className="h-4 w-4" />
          </div>
        </div>
      )}

      {/* Expiry Warning for Waiting Rooms */}
      {showExpiryWarning && room.status === 'waiting' && (
        <div className="bg-orange-500/20 border-b border-orange-500/50 px-4 py-2">
          <div className="max-w-7xl mx-auto flex items-center justify-center gap-2 text-orange-400">
            <Clock className="h-4 w-4 animate-pulse" />
            <span className="text-sm font-medium">
              Room expires in {Math.floor(expiryCountdown / 60)}:{(expiryCountdown % 60).toString().padStart(2, '0')} if no one joins
            </span>
          </div>
        </div>
      )}

      {/* Waiting State */}
      {room.status === 'waiting' && (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center p-8 max-w-md">
            {/* Topic Image (if exists) */}
            {room.topic_image_url && (
              <div className="mb-6">
                <img 
                  src={`${API_URL}${room.topic_image_url}`} 
                  alt={room.topic}
                  className="w-full max-h-48 object-cover rounded-xl"
                />
              </div>
            )}

            <h2 className="text-xl font-bold text-white mb-2">{room.topic}</h2>
            
            {room.description && (
              <p className="text-gray-400 text-sm mb-4">{room.description}</p>
            )}

            {/* Creator Info */}
            <div className="flex items-center justify-center gap-3 mb-6 p-3 bg-white/5 rounded-lg">
              <img 
                src={room.participant_a_picture || '/logo.png'}
                alt={room.participant_a_name}
                className="w-12 h-12 rounded-full border-2 border-blue-500"
              />
              <div className="text-left">
                <p className="text-white font-medium">{room.participant_a_name}</p>
                <p className="text-xs text-gray-400">Created this debate</p>
              </div>
            </div>

            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-orange-500/20 flex items-center justify-center">
              <Clock className="h-8 w-8 text-orange-500 animate-pulse" />
            </div>
            
            <p className="text-gray-400 mb-6 text-sm">
              {room.room_type === 'private' 
                ? 'Waiting for invited opponent to join...'
                : 'Waiting for opponent...'}
            </p>
            
            {!isParticipant && user && room.room_type === 'public' && (
              <Button
                onClick={handleJoinRoom}
                className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 px-8"
                data-testid="join-room-btn"
              >
                <Swords className="h-5 w-5 mr-2" />
                Join Debate
              </Button>
            )}
            
            {!isParticipant && user && room.room_type === 'private' && room.participant_b_id === user.user_id && (
              <Button
                onClick={handleJoinRoom}
                className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
              >
                <Swords className="h-5 w-5 mr-2" />
                Accept Challenge
              </Button>
            )}

            {!user && (
              <Button
                onClick={() => navigate('/login')}
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10"
              >
                Login to Join
              </Button>
            )}

            {/* Delete Room Button (Creator only, no opponent joined) */}
            {isCreator && !room.participant_b_id && (
              <div className="mt-6 pt-6 border-t border-white/10">
                {showDeleteConfirm ? (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-400">Are you sure you want to delete this room?</p>
                    <div className="flex gap-2 justify-center">
                      <Button
                        onClick={handleDeleteRoom}
                        disabled={deleting}
                        size="sm"
                        className="bg-red-500 hover:bg-red-600"
                        data-testid="confirm-delete-room-btn"
                      >
                        {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1" />}
                        Yes, Delete
                      </Button>
                      <Button
                        onClick={() => setShowDeleteConfirm(false)}
                        size="sm"
                        variant="outline"
                        className="border-white/20 text-white"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button
                    onClick={() => setShowDeleteConfirm(true)}
                    variant="ghost"
                    size="sm"
                    className="text-gray-400 hover:text-red-400 hover:bg-red-500/10"
                    data-testid="delete-room-btn"
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Delete Room
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Active Debate - includes 'ready' and 'live' states */}
      {(room.status === 'ready' || room.status === 'live') && (
        <>
          {/* Ready State Banner - Opponent joined, waiting for messages */}
          {room.status === 'ready' && (
            <div className="bg-gradient-to-r from-blue-600/30 to-purple-600/30 border-b border-blue-500/50 px-4 py-4">
              <div className="max-w-7xl mx-auto">
                <div className="flex items-center justify-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                    <CheckCircle className="h-5 w-5 text-green-400" />
                  </div>
                  <div className="text-center">
                    <p className="text-white font-semibold">Opponent joined!</p>
                    <p className="text-sm text-blue-300">Start the conversation to begin the debate</p>
                  </div>
                </div>
                
                {/* Message progress indicators */}
                <div className="flex items-center justify-center gap-8 mt-4">
                  <div className="flex items-center gap-2">
                    <img 
                      src={room.participant_a_picture || '/logo.png'} 
                      alt={room.participant_a_name}
                      className="w-8 h-8 rounded-full border-2 border-blue-500"
                    />
                    <div className="text-sm">
                      <p className="text-gray-300">{room.participant_a_name?.split(' ')[0]}</p>
                      {participantAMessageCount >= 1 ? (
                        <span className="text-green-400 flex items-center gap-1">
                          <CheckCircle className="h-3 w-3" /> Ready
                        </span>
                      ) : (
                        <span className="text-yellow-400 text-xs">Waiting for message...</span>
                      )}
                    </div>
                  </div>
                  
                  <div className="text-gray-500">VS</div>
                  
                  <div className="flex items-center gap-2">
                    <img 
                      src={room.participant_b_picture || '/logo.png'} 
                      alt={room.participant_b_name}
                      className="w-8 h-8 rounded-full border-2 border-red-500"
                    />
                    <div className="text-sm">
                      <p className="text-gray-300">{room.participant_b_name?.split(' ')[0]}</p>
                      {participantBMessageCount >= 1 ? (
                        <span className="text-green-400 flex items-center gap-1">
                          <CheckCircle className="h-3 w-3" /> Ready
                        </span>
                      ) : (
                        <span className="text-yellow-400 text-xs">Waiting for message...</span>
                      )}
                    </div>
                  </div>
                </div>
                
                {isParticipant && (
                  <p className="text-center text-xs text-gray-400 mt-3">
                    Debate starts when both participants send at least one message
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Debate Live Banner */}
          {room.status === 'live' && debateTimerWarning && (
            <div className="bg-red-500/20 border-b border-red-500/50 px-4 py-2">
              <div className="max-w-7xl mx-auto flex items-center justify-center gap-2 text-red-400">
                <Timer className="h-4 w-4 animate-pulse" />
                <span className="text-sm font-medium">
                  Debate ends in {Math.floor((debateTimeRemaining || 0) / 60)}:{((debateTimeRemaining || 0) % 60).toString().padStart(2, '0')}
                </span>
              </div>
            </div>
          )}

          {/* Topic Image Banner (if exists) */}
          {room.topic_image_url && (
            <div className="relative h-32 overflow-hidden">
              <img 
                src={`${API_URL}${room.topic_image_url}`} 
                alt={room.topic}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-transparent to-gray-900" />
            </div>
          )}

          {/* Main Debate Area */}
          <div className="flex-1 flex overflow-hidden">
            {/* Participant A Side */}
            <div className="flex-1 flex flex-col border-r border-white/10">
              {/* Participant A Header */}
              <div className="p-4 bg-blue-500/10 border-b border-white/10">
                <div className="flex items-center gap-3">
                  <img 
                    src={room.participant_a_picture || '/logo.png'} 
                    alt={room.participant_a_name}
                    className="w-10 h-10 rounded-full border-2 border-blue-500"
                  />
                  <div>
                    <p className="font-semibold text-white">{room.participant_a_name}</p>
                    <p className="text-xs text-blue-400">Side A</p>
                  </div>
                  {room.room_type === 'public' && (
                    <div className="ml-auto text-center">
                      <p className="text-2xl font-bold text-blue-400">{room.vote_a_count}</p>
                      <p className="text-xs text-gray-500">votes</p>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Messages A */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {messagesA.map((msg) => (
                  <div key={msg.message_id} className="bg-blue-500/20 rounded-lg p-3">
                    <p className="text-white">{msg.message_text}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(msg.created_at).toLocaleTimeString()}
                    </p>
                  </div>
                ))}
                <div ref={isParticipantA ? messagesEndRef : null} />
              </div>
            </div>
            
            {/* Participant B Side */}
            <div className="flex-1 flex flex-col">
              {/* Participant B Header */}
              <div className="p-4 bg-red-500/10 border-b border-white/10">
                <div className="flex items-center gap-3">
                  <img 
                    src={room.participant_b_picture || '/logo.png'} 
                    alt={room.participant_b_name}
                    className="w-10 h-10 rounded-full border-2 border-red-500"
                  />
                  <div>
                    <p className="font-semibold text-white">{room.participant_b_name}</p>
                    <p className="text-xs text-red-400">Side B</p>
                  </div>
                  {room.room_type === 'public' && (
                    <div className="ml-auto text-center">
                      <p className="text-2xl font-bold text-red-400">{room.vote_b_count}</p>
                      <p className="text-xs text-gray-500">votes</p>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Messages B */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {messagesB.map((msg) => (
                  <div key={msg.message_id} className="bg-red-500/20 rounded-lg p-3">
                    <p className="text-white">{msg.message_text}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(msg.created_at).toLocaleTimeString()}
                    </p>
                  </div>
                ))}
                <div ref={isParticipantB ? messagesEndRef : null} />
              </div>
            </div>
          </div>

          {/* Audience Panel (Public Rooms) */}
          {room.room_type === 'public' && (
            <div className="bg-black/30 border-t border-white/10 px-4 py-3">
              <div className="max-w-7xl mx-auto flex items-center justify-between">
                <div className="flex items-center gap-2 text-gray-400">
                  <Eye className="h-4 w-4" />
                  <span className="text-sm">
                    {(room.vote_a_count || 0) + (room.vote_b_count || 0) + (reactions.total || 0)} watching
                  </span>
                </div>
                
                {/* Reactions */}
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => handleReact('fire')}
                    className="flex items-center gap-1 text-orange-400 hover:scale-110 transition-transform"
                    disabled={!user}
                  >
                    <Flame className="h-5 w-5" />
                    <span>{reactions.fire}</span>
                  </button>
                  <button
                    onClick={() => handleReact('thumbs_up')}
                    className="flex items-center gap-1 text-blue-400 hover:scale-110 transition-transform"
                    disabled={!user}
                  >
                    <ThumbsUp className="h-5 w-5" />
                    <span>{reactions.thumbs_up}</span>
                  </button>
                  <button
                    onClick={() => handleReact('laugh')}
                    className="flex items-center gap-1 text-yellow-400 hover:scale-110 transition-transform"
                    disabled={!user}
                  >
                    <Laugh className="h-5 w-5" />
                    <span>{reactions.laugh}</span>
                  </button>
                </div>
                
                {/* Voting (for audience only) */}
                {isAudience && user && !userVote && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-400 mr-2">Vote:</span>
                    <Button
                      size="sm"
                      onClick={() => handleVote('A')}
                      className="bg-blue-500 hover:bg-blue-600"
                    >
                      <Crown className="h-4 w-4 mr-1" /> {room.participant_a_name?.split(' ')[0]}
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleVote('B')}
                      className="bg-red-500 hover:bg-red-600"
                    >
                      <Crown className="h-4 w-4 mr-1" /> {room.participant_b_name?.split(' ')[0]}
                    </Button>
                  </div>
                )}
                
                {userVote && (
                  <p className="text-sm text-gray-400">
                    Voted for {userVote === 'A' ? room.participant_a_name : room.participant_b_name}
                  </p>
                )}
                
                {/* Toggle Audience Comments */}
                <button
                  onClick={() => setShowAudiencePanel(!showAudiencePanel)}
                  className="flex items-center gap-1 text-purple-400 hover:text-purple-300 transition-colors"
                >
                  <MessageCircle className="h-4 w-4" />
                  <span className="text-sm">{audienceComments.length} Comments</span>
                </button>
              </div>
            </div>
          )}

          {/* Audience Comments Panel (Public Rooms) */}
          {room.room_type === 'public' && showAudiencePanel && (
            <div className="bg-black/40 border-t border-white/10 max-h-48 flex flex-col">
              <div className="px-4 py-2 border-b border-white/10 flex items-center gap-2">
                <MessageCircle className="h-4 w-4 text-purple-400" />
                <span className="text-sm font-medium text-white">Audience Comments</span>
                <span className="text-xs text-gray-500">({audienceComments.length})</span>
              </div>
              
              {/* Comments List */}
              <div className="flex-1 overflow-y-auto px-4 py-2 space-y-2">
                {audienceComments.length > 0 ? (
                  audienceComments.map((comment) => (
                    <div key={comment.comment_id} className="flex items-start gap-2">
                      <img
                        src={comment.user_picture || '/logo.png'}
                        alt={comment.user_name}
                        className="w-6 h-6 rounded-full flex-shrink-0"
                        onError={(e) => { e.target.src = '/logo.png'; }}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2">
                          <span className="text-xs font-medium text-purple-400">{comment.user_name}</span>
                          <span className="text-xs text-gray-600">
                            {new Date(comment.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                        <p className="text-sm text-gray-300 break-words">{comment.text}</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-center text-gray-500 text-sm py-2">No comments yet. Be the first!</p>
                )}
              </div>
              
              {/* Comment Input (for audience only) */}
              {isAudience && user && (
                <form onSubmit={handlePostAudienceComment} className="px-4 py-2 border-t border-white/10 flex gap-2">
                  <Input
                    value={newAudienceComment}
                    onChange={(e) => setNewAudienceComment(e.target.value)}
                    placeholder="Add a comment..."
                    className="flex-1 h-8 text-sm bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                    maxLength={200}
                    data-testid="audience-comment-input"
                  />
                  <Button
                    type="submit"
                    size="sm"
                    disabled={!newAudienceComment.trim() || sendingComment}
                    className="h-8 bg-purple-500 hover:bg-purple-600"
                    data-testid="send-audience-comment-btn"
                  >
                    {sendingComment ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                  </Button>
                </form>
              )}
              
              {!user && (
                <div className="px-4 py-2 border-t border-white/10 text-center">
                  <button 
                    onClick={() => navigate('/login')} 
                    className="text-sm text-purple-400 hover:text-purple-300"
                  >
                    Login to comment
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Message Input (Participants Only) */}
          {isParticipant && (
            <div className="bg-black/50 border-t border-white/10 p-4">
              <form onSubmit={handleSendMessage} className="max-w-4xl mx-auto flex gap-3">
                <Input
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type your argument..."
                  className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                  maxLength={500}
                  data-testid="room-message-input"
                />
                <Button 
                  type="submit" 
                  disabled={!newMessage.trim() || sending}
                  className="bg-gradient-to-r from-orange-500 to-red-500"
                  data-testid="send-message-btn"
                >
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </form>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Room;
